import React, { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TButtonProps,
  TextField,
  Typography,
} from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { TestId } from '@modules/utilities/constants/testConstants';
import {
  MaxNumCharsExceededErrorSnack,
  MinNumWordsRequiredErrorSnack,
} from '@modules/miscellaneous/common/components/ErrorSnackbar';
import { FeedbackRating } from '@rbx/conv-ai-provider';
import useShareFeedbackStyles from './ShareFeedback.styles';
import FeedbackPositiveIcon from './FeedbackPositiveIcon';
import FeedbackNegativeIcon from './FeedbackNegativeIcon';
import { SubmitFeedbackCallback, useShareFeedbackProvider } from '../context/ShareFeedbackProvider';
import ShareFeedbackControlOrGuidelines from './ShareFeedbackControlOrGuidelines';

interface ShareGeneralFeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  submitFeedback: SubmitFeedbackCallback;
  isPublicDevForumFeedbackAllowed?: boolean;
}

const ShareGeneralFeedbackDialog: FC<React.PropsWithChildren<ShareGeneralFeedbackDialogProps>> = ({
  open,
  onClose: closeDialog,
  submitFeedback,
  isPublicDevForumFeedbackAllowed = false,
}) => {
  const { translate } = useTranslation();
  const [rating, setRating] = useState<FeedbackRating>();
  const [comment, setComment] = useState<string>('');
  // user must opt in to post to devforum, every time
  // the feedback form is mounted
  const initialIsFeedbackPublic = false;
  const [isFeedbackPublic, setIsFeedbackPublic] = useState<boolean>(initialIsFeedbackPublic);
  const [showMaxWordsExceeded, setShowMaxWordsExceeded] = useState<boolean>(false);
  const [showMinWordsRequired, setShowMinWordsRequired] = useState<boolean>(false);
  const {
    classes: { container, textarea, textareaOutline, buttonIcon, actions },
  } = useShareFeedbackStyles();
  const { descriptionLength } = useShareFeedbackProvider();
  const disabled = useMemo(() => !rating, [rating]);
  const onChangeComment = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const cmmt = e.currentTarget.value;
      setComment(cmmt);
      if (cmmt && cmmt.length > descriptionLength.max) {
        setShowMaxWordsExceeded(true);
      }
    },
    [descriptionLength.max],
  );
  const commentPlaceholder = useMemo(
    () =>
      translate('Message.NoPrivatePersonalInfoWithMinNumCharacters', {
        minNumCharacters: descriptionLength.min.toLocaleString(),
      }),
    [translate, descriptionLength.min],
  );
  const onRatingClick = useCallback(
    (ratingVal: FeedbackRating) => () => {
      setRating(ratingVal);
    },
    [],
  );
  const onConfirm = useCallback(() => {
    if (comment && comment.length > descriptionLength.max) {
      setShowMaxWordsExceeded(true);
    } else if (comment && comment.length < descriptionLength.min) {
      setShowMinWordsRequired(true);
    } else if (rating) {
      submitFeedback(rating, comment, {
        isPublic: isFeedbackPublic,
      });
      closeDialog();
    }
  }, [rating, descriptionLength, comment, closeDialog, submitFeedback, isFeedbackPublic]);
  const onClose = useCallback(() => {
    closeDialog();
  }, [closeDialog]);
  const rateButtonProps: TButtonProps = useMemo(
    () => ({
      size: 'large',
      color: 'secondary',
    }),
    [],
  );
  const getButtonVariant = useCallback(
    (ratingVal: FeedbackRating) => (rating === ratingVal ? 'contained' : 'outlined'),
    [rating],
  );
  return (
    <React.Fragment>
      <Dialog
        data-testid={TestId.FeedbackDialog}
        tabIndex={-1}
        open={open}
        sx={{
          maxWidth: 535,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
        onClose={onClose}
        PaperProps={{
          className: container,
        }}>
        <DialogTitle>
          <Typography
            variant='h3'
            component='h3'
            display='flex'
            alignItems='center'
            justifyContent='left'>
            {translate('Message.WasThisPageHelpful')}
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            overflow: 'visible',
          }}>
          <Grid container direction='column'>
            <Grid item XSmall={12} marginTop={2} marginBottom={4}>
              <Grid container columnGap={2}>
                <Grid item XSmall={3}>
                  <Button
                    fullWidth
                    data-testid={TestId.FeedbackDialogPositiveRatingButton}
                    onClick={onRatingClick(FeedbackRating.Positive)}
                    aria-label={translate('Label.RatePositively')}
                    {...rateButtonProps}
                    variant={getButtonVariant(FeedbackRating.Positive)}>
                    <FeedbackPositiveIcon
                      className={buttonIcon}
                      filled={rating === FeedbackRating.Positive}
                      size={20}
                    />{' '}
                    {translate('Label.Yes')}
                  </Button>
                </Grid>
                <Grid item XSmall={3}>
                  <Button
                    fullWidth
                    onClick={onRatingClick(FeedbackRating.Negative)}
                    aria-label={translate('Label.RateNegatively')}
                    {...rateButtonProps}
                    variant={getButtonVariant(FeedbackRating.Negative)}>
                    <FeedbackNegativeIcon
                      className={buttonIcon}
                      filled={rating === FeedbackRating.Negative}
                      size={20}
                    />{' '}
                    {translate('Label.No')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid item marginBottom={0.5}>
              <Typography variant='body1' component='label' color='primary'>
                {translate('Message.TellUsWhyOptional')}
              </Typography>
            </Grid>
            <Grid item>
              <TextField
                id='general-feedback-comment'
                data-testid={TestId.FeedbackDialogCommentInput}
                className={textarea}
                InputProps={{
                  classes: {
                    notchedOutline: textareaOutline,
                  },
                }}
                value={comment}
                size='medium'
                variant='outlined'
                fullWidth
                multiline
                rows={5}
                label=''
                aria-label={commentPlaceholder}
                placeholder={commentPlaceholder}
                onChange={onChangeComment}
              />
            </Grid>
            <ShareFeedbackControlOrGuidelines
              isPublicDevForumFeedbackAllowed={isPublicDevForumFeedbackAllowed}
              isFeedbackPublic={isFeedbackPublic}
              setIsFeedbackPublic={setIsFeedbackPublic}
            />
          </Grid>
        </DialogContent>
        <DialogActions className={actions}>
          <Button
            variant='outlined'
            aria-label={translate('Action.Cancel')}
            color='primary'
            onClick={onClose}
            disabled={false}>
            {translate('Action.Cancel')}
          </Button>
          <Button
            variant='contained'
            data-testid={TestId.FeedbackDialogSubmitBtn}
            loading={disabled}
            aria-label={translate('Action.Submit')}
            color='primaryBrand'
            onClick={onConfirm}>
            {translate('Action.Submit')}
          </Button>
        </DialogActions>
      </Dialog>
      <MinNumWordsRequiredErrorSnack
        open={showMinWordsRequired}
        minNumChars={descriptionLength.min}
        onClose={() => setShowMinWordsRequired(false)}
      />
      <MaxNumCharsExceededErrorSnack
        open={showMaxWordsExceeded}
        maxNumChars={descriptionLength.max}
        onClose={() => setShowMaxWordsExceeded(false)}
      />
    </React.Fragment>
  );
};

export default ShareGeneralFeedbackDialog;
